import { renderAsync } from 'docx-preview';
import { DownloadIcon, Loader2Icon, PencilIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { OnlyOfficeEditor } from '@/components/onlyoffice/OnlyOfficeEditor';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLazyGetBlobsQuery } from '@/services/api/blobService';
import {
  type EditorConfig,
  useLazyGetBlobEditorConfigQuery,
} from '@/services/api/onlyofficeService';
import { Blob } from '@/services/types/blob-types';

const PDF_POLL_INTERVAL_MS = 3000;
const PDF_POLL_MAX_ATTEMPTS = 20;

interface DocxPreviewProps {
  file: Blob;
  onLoad?: () => void;
  isLoading?: boolean;
  matterId?: string;
}

export const DocxPreview = ({
  file: initialFile,
  onLoad,
  isLoading: externalLoading,
  matterId,
}: DocxPreviewProps) => {
  const [file, setFile] = useState<Blob>(initialFile);
  const [internalLoading, setInternalLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const onLoadRef = useRef(onLoad);
  const loadingDownloadUrlRef = useRef<string | null>(null);

  // Keep file in sync with prop when it changes externally
  useEffect(() => {
    setFile(initialFile);
  }, [initialFile]);

  // OnlyOffice editor state
  const [editorConfig, setEditorConfig] = useState<EditorConfig | null>(null);
  const [getEditorConfig, { isFetching: isLoadingConfig }] =
    useLazyGetBlobEditorConfigQuery();
  const [getBlobs] = useLazyGetBlobsQuery();

  useEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  const isLoading = externalLoading ?? internalLoading;

  const hasPdfPreview = !!file.pdf_preview_url;

  const loadDocument = useCallback(async () => {
    // Skip client-side DOCX rendering when a server-side PDF preview exists
    if (hasPdfPreview) return;
    if (!containerRef.current || !file.download_url) return;

    const currentUrl = file.download_url;
    if (loadingDownloadUrlRef.current === currentUrl) return;

    loadingDownloadUrlRef.current = currentUrl;
    setInternalLoading(true);
    setError(null);

    try {
      const response = await fetch(currentUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      if (loadingDownloadUrlRef.current === currentUrl && containerRef.current) {
        containerRef.current.innerHTML = '';

        await renderAsync(arrayBuffer, containerRef.current, undefined, {
          className: 'docx-preview-content',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: true,
          experimental: false,
          trimXmlDeclaration: true,
          useBase64URL: true,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
        });

        if (loadingDownloadUrlRef.current === currentUrl) {
          setInternalLoading(false);
          onLoadRef.current?.();
        }
      }
    } catch (err) {
      if (loadingDownloadUrlRef.current === currentUrl) {
        console.error('Error loading docx:', err);
        setError(err instanceof Error ? err.message : 'Failed to load document');
        setInternalLoading(false);
        onLoadRef.current?.();
      }
    } finally {
      if (loadingDownloadUrlRef.current === currentUrl) {
        loadingDownloadUrlRef.current = null;
      }
    }
  }, [file.download_url, hasPdfPreview]);

  useEffect(() => {
    loadingDownloadUrlRef.current = null;
    loadDocument();
  }, [loadDocument]);

  const handleEdit = async () => {
    if (!matterId) return;
    try {
      const config = await getEditorConfig({
        matterId,
        blobId: file.id,
      }).unwrap();
      setEditorConfig(config);
    } catch {
      toast.error('Failed to load editor');
    }
  };

  const [generatingPreview, setGeneratingPreview] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const handleEditorClose = async () => {
    setEditorConfig(null);
    // Show spinner immediately — before any async work — so there's no flash
    setGeneratingPreview(true);

    if (!matterId) {
      setGeneratingPreview(false);
      loadingDownloadUrlRef.current = null;
      loadDocument();
      return;
    }

    // After OnlyOffice saves, the backend clears pdf_preview (sets it to null)
    // then regenerates it asynchronously via Temporal (~5-7s total).
    //
    // With `forcesave: true` the save may have already been processed before
    // the user closes the editor, so the null→present transition may never
    // happen.  Instead of tracking that transition we use a minimum-wait
    // approach: poll at least MIN_POLLS times (to let the close-callback +
    // regeneration finish), then accept the first poll that shows a preview.
    let attempts = 0;
    const MIN_POLLS_BEFORE_ACCEPTING = 3; // 9s — enough for callback + PDF generation

    const poll = async () => {
      attempts += 1;
      try {
        const blobs = await getBlobs(matterId).unwrap();
        const updated = blobs?.find((b) => b.id === file.id);

        if (updated?.pdf_preview_url && attempts >= MIN_POLLS_BEFORE_ACCEPTING) {
          // Preview URL is present after the grace period — show it
          setFile(updated);
          setGeneratingPreview(false);
          return;
        }
      } catch {
        // Keep polling
      }

      if (attempts < PDF_POLL_MAX_ATTEMPTS) {
        pollRef.current = setTimeout(poll, PDF_POLL_INTERVAL_MS);
      } else {
        // Give up polling — refetch one last time and show whatever we have
        try {
          const blobs = await getBlobs(matterId).unwrap();
          const updated = blobs?.find((b) => b.id === file.id);
          if (updated) setFile(updated);
        } catch {
          // ignore
        }
        setGeneratingPreview(false);
        setInternalLoading(false);
        // Force DOCX re-render in case the container remounted but
        // loadDocument's deps (download_url, hasPdfPreview) didn't change.
        loadingDownloadUrlRef.current = null;
        loadDocument();
      }
    };

    pollRef.current = setTimeout(poll, PDF_POLL_INTERVAL_MS);
  };

  if (editorConfig) {
    return (
      <OnlyOfficeEditor
        editorConfig={editorConfig}
        onClose={handleEditorClose}
        title={file.name}
      />
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 p-4">
        <p className="text-sm text-muted-foreground font-medium">
          Unable to preview document
        </p>
        <p className="text-xs text-muted-foreground">{error}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const a = document.createElement('a');
            a.href = file.download_url;
            a.download = file.name;
            a.click();
          }}
        >
          <DownloadIcon className="h-4 w-4 mr-2" />
          Download File
        </Button>
      </div>
    );
  }

  return (
    <>
      {(isLoading || generatingPreview) && (
        <div className="absolute inset-0 flex items-center justify-center backdrop-blur-[1px] z-10">
          <div className="flex flex-col items-center gap-2 bg-background/70 p-4 rounded-lg">
            <Loader2Icon className="h-5 w-5 animate-spin text-primary" />
            <span className="text-xs text-muted-foreground font-medium">
              {generatingPreview ? 'Generating preview...' : 'Loading document...'}
            </span>
          </div>
        </div>
      )}
      {!generatingPreview &&
        (hasPdfPreview ? (
          <iframe
            src={file.pdf_preview_url!}
            className={cn(
              'absolute inset-0 w-full h-full transition-opacity duration-500 z-[1]',
              isLoading ? 'opacity-0' : 'opacity-100',
            )}
            title={file.name}
            allow="fullscreen"
            onLoad={() => {
              setInternalLoading(false);
              onLoadRef.current?.();
            }}
            onError={() => {
              setError('Failed to load PDF preview');
              setInternalLoading(false);
              onLoadRef.current?.();
            }}
          />
        ) : (
          <div
            ref={containerRef}
            className={cn(
              'absolute inset-0 w-full h-full transition-opacity duration-500 z-[1] overflow-auto docx-preview-container',
              isLoading ? 'opacity-0' : 'opacity-100',
            )}
            style={{
              backgroundColor: '#f5f5f5',
            }}
          />
        ))}
      {!isLoading && !error && !generatingPreview && (
        <div className="absolute bottom-2 right-2 z-20 flex gap-1.5">
          {matterId && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleEdit}
              disabled={isLoadingConfig}
              className="h-7 text-xs px-2 bg-background/80 backdrop-blur-sm"
            >
              {isLoadingConfig ? (
                <Loader2Icon className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <PencilIcon className="h-3 w-3 mr-1" />
              )}
              Edit
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const a = document.createElement('a');
              a.href = file.download_url;
              a.download = file.name;
              a.click();
            }}
            className="h-7 text-xs px-2 bg-background/80 backdrop-blur-sm"
          >
            <DownloadIcon className="h-3 w-3 mr-1" />
            Download
          </Button>
        </div>
      )}
    </>
  );
};

export default DocxPreview;
